/**
 * Lease Price Calculator for Bike Configurator
 * Toont de leaseprijs van de geselecteerde variant op de productpagina.
 *
 * Er wordt hier niets berekend: sections/main-product.liquid rendert per variant alle bedragen
 * (#lease-variant-data) via snippets/lease-lookup.liquid, waar de rekenregels staan (leasebasis =
 * adviesprijs als die hoger is, staffel, alleen zakelijk vanaf €5.000, actie). Deze calculator kiest
 * het bedrag bij de gekozen variant, het lease type en de termijn.
 *
 * Security: Input validation, no innerHTML, sanitized output.
 */

(function() {
  'use strict';

  const DEBUG = false;

  function debugLog(...args) {
    if (DEBUG) console.log('[LeaseCalculator]', ...args);
  }

  class LeaseCalculator extends HTMLElement {
    constructor() {
      super();
      this.leaseType = 'prive';
      this.leaseTerm = '48';
      this.variants = {};
      this.promo = { pct: 0, months: 0, label: '', bg: '', fg: '' };
      this.variant = null;

      // Verkoopprijs en leasebasis (euro) van de gekozen variant
      this.basePrice = 0;
      this.leaseBasePrice = 0;

      // Store both prices for comparison display
      this.privePrice = null;
      this.zakelijkPrice = null;
    }

    connectedCallback() {
      var selected = this.loadVariantData();
      this.initializeCalculator(selected);
    }

    /**
     * Lees de leasebedragen per variant uit #lease-variant-data.
     * @returns {string|null} id van de variant waarmee de pagina geladen is
     */
    loadVariantData() {
      var script = document.getElementById('lease-variant-data');
      if (!script) {
        debugLog('Lease variant data not found.');
        return null;
      }

      try {
        var data = JSON.parse(script.textContent);
        this.variants = (data && typeof data.variants === 'object' && data.variants) || {};
        if (data && data.promo) {
          // Actie uit snippets/lease-lookup.liquid (metaobject lease_actie); kleuren alleen als hex
          var hex = function (c) { return (typeof c === 'string' && /^#[0-9a-f]{3,8}$/i.test(c)) ? c : ''; };
          this.promo = {
            pct: this.validateNumber(data.promo.pct, 0, 100) || 0,
            months: this.validateNumber(data.promo.months, 0, 120) || 0,
            label: typeof data.promo.label === 'string' ? data.promo.label : '',
            bg: hex(data.promo.bg),
            fg: hex(data.promo.fg)
          };
        }
        return data && data.selected != null ? String(data.selected) : null;
      } catch (e) {
        debugLog('Failed to parse lease variant data:', e);
        this.variants = {};
        return null;
      }
    }

    /**
     * Validate a number is finite and within reasonable bounds
     */
    validateNumber(value, min, max) {
      if (min === undefined) min = 0;
      if (max === undefined) max = 1000000;

      var num = parseFloat(value);
      if (typeof num !== 'number' || !isFinite(num) || isNaN(num)) {
        return null;
      }
      if (num < min || num > max) {
        return null;
      }
      return num;
    }

    /** Bedrag in centen uit het overzicht naar euro's, of null */
    cents(value) {
      var c = this.validateNumber(value, 0, 100000000);
      return c === null ? null : c / 100;
    }

    initializeCalculator(selectedId) {
      this.setupEventListeners();
      this.selectVariant(selectedId);
    }

    /**
     * Kies de variant waarvan de leaseprijs getoond wordt en reken alles opnieuw door.
     * Onbekende id's (bijv. een niet-bestaande combinatie) laten de huidige variant staan.
     */
    selectVariant(variantId) {
      var variant = variantId ? this.variants[String(variantId)] : null;
      if (!variant || typeof variant !== 'object') {
        debugLog('Unknown variant, keeping current:', variantId);
        if (!this.variant) this.updateLeasePrice();
        return;
      }

      this.variant = variant;
      this.basePrice = this.cents(variant.price) || 0;
      this.leaseBasePrice = this.cents(variant.basis) || 0;
      this.setZakelijkOnly(variant.zakelijk_only === true);
      this.updateLeasePrice();
    }

    /**
     * Alleen-zakelijk-varianten (leasebasis >= €5.000): forceer zakelijk 36 mnd en verberg privé en 48 mnd.
     * Voor andere varianten worden die knoppen weer getoond.
     */
    setZakelijkOnly(zakelijkOnly) {
      var hidden = zakelijkOnly ? 'none' : '';
      var priveRadio = document.getElementById('lease_type_prive');
      var priveLabel = document.querySelector('label[for="lease_type_prive"]');
      var term48Radio = document.getElementById('lease_termijn_48mnd');
      var term48Label = document.querySelector('label[for="lease_termijn_48mnd"]');
      if (priveRadio) priveRadio.style.display = hidden;
      if (priveLabel) priveLabel.style.display = hidden;
      if (term48Radio) term48Radio.style.display = hidden;
      if (term48Label) term48Label.style.display = hidden;

      if (!zakelijkOnly) return;

      this.leaseType = 'zakelijk';
      this.leaseTerm = '36';

      var zakelijkRadio = document.getElementById('lease_type_zakelijk');
      if (zakelijkRadio && zakelijkRadio.type === 'radio') {
        zakelijkRadio.checked = true;
      }

      var term36Radio = document.getElementById('lease_termijn_36mnd');
      if (term36Radio && term36Radio.type === 'radio') {
        term36Radio.checked = true;
      }

      try {
        sessionStorage.setItem('leaseType', this.leaseType);
        sessionStorage.setItem('leaseTerm', this.leaseTerm);
      } catch (e) {
        debugLog('Error saving to sessionStorage:', e);
      }

      debugLog('Enforced zakelijk-only mode (lease basis >= 5000)');
    }

    setupEventListeners() {
      var self = this;

      // Lease type changes (prive/zakelijk)
      var leaseTypeInputs = document.querySelectorAll('input[name="lease_type"]');
      leaseTypeInputs.forEach(function(input) {
        input.addEventListener('change', function(e) {
          var value = e.target.value;
          if (value !== 'prive' && value !== 'zakelijk') return;

          self.leaseType = value;

          if (self.leaseType === 'zakelijk') {
            self.leaseTerm = '36';
            var term36Radio = document.getElementById('lease_termijn_36mnd');
            if (term36Radio && term36Radio.type === 'radio') {
              term36Radio.checked = true;
            }
          } else {
            self.leaseTerm = '48';
            var term48Radio = document.getElementById('lease_termijn_48mnd');
            if (term48Radio && term48Radio.type === 'radio') {
              term48Radio.checked = true;
            }
          }

          self.updateLeasePrice();
        });
      });

      // Lease term changes (36/48 months)
      var leaseTermInputs = document.querySelectorAll('input[name="lease_term"]');
      leaseTermInputs.forEach(function(input) {
        input.addEventListener('change', function(e) {
          var value = e.target.value;
          if (value !== '36' && value !== '48') return;

          self.leaseTerm = value;
          self.updateLeasePrice();
        });
      });

      // Variantwissel (kleur, maat, accu, ...): product-info.js publiceert na elke wissel de nieuwe
      // variant via pubsub (assets/pubsub.js + constants.js). Varianten van andere producten
      // (bijv. een quick view) staan niet in het overzicht en worden genegeerd.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, function(event) {
          var variant = event && event.data && event.data.variant;
          if (variant && variant.id) self.selectVariant(variant.id);
        });
      } else {
        debugLog('pubsub not available; variant changes will not update the lease price.');
      }
    }

    /** Actieprijs (privé) bij de gekozen termijn, of null */
    promoPrice() {
      if (!this.variant) return null;
      return this.cents(this.variant['prive_' + this.leaseTerm + '_promo']);
    }

    /** "3 maanden" of "1 maand" */
    get promoMonthsText() {
      return this.promo.months + (this.promo.months === 1 ? ' maand' : ' maanden');
    }

    get promoBadgeLabel() {
      return this.promo.label || ('Nu ' + this.promoMonthsText + ' ' + this.promo.pct + '% korting');
    }

    updateLeasePrice() {
      var v = this.variant || {};

      // Privé met de gekozen termijn; zakelijk altijd 36 maanden
      this.privePrice = (this.leaseTerm === '36' || this.leaseTerm === '48') ? this.cents(v['prive_' + this.leaseTerm]) : null;
      this.zakelijkPrice = this.cents(v.zakelijk_36);

      // Get the price for the selected lease type
      var monthlyPrice = (this.leaseType === 'prive') ? this.privePrice : this.zakelijkPrice;

      if (monthlyPrice === null) {
        this.displayError();
        this.notifyChange();
        return;
      }

      var promoPrice = this.promoPrice();

      // Update main lease price display
      var leaseDisplayElement = document.getElementById('lease-price-display');
      if (leaseDisplayElement) {
        this.renderLeasePrice(leaseDisplayElement, monthlyPrice, promoPrice);
        leaseDisplayElement.classList.remove('hidden');
        leaseDisplayElement.classList.remove('error');

        // Expose current lease price as dataset so other scripts (e.g. lease-button.js)
        // can read it without parsing text content.
        // BELANGRIJK: currentLeasePrice = NORMALE prijs. De actie is puur visueel;
        // het externe lease-formulier moet de echte (normale) maandprijs krijgen.
        leaseDisplayElement.dataset.currentLeasePrice = monthlyPrice.toFixed(2);

        // Actieprijs + maanden alleen voor de weergave in de samenvatting op de aanvraagpagina
        // (lease-button.js zet ze in leaseProductData). Nooit als echte prijs gebruiken.
        if (this.leaseType === 'prive' && promoPrice !== null) {
          leaseDisplayElement.dataset.currentPromoPrice = promoPrice.toFixed(2);
          leaseDisplayElement.dataset.currentPromoMonths = String(this.promo.months);
        } else {
          delete leaseDisplayElement.dataset.currentPromoPrice;
          delete leaseDisplayElement.dataset.currentPromoMonths;
        }
      }

      // Update total price display
      var totalPriceElement = document.getElementById('total-bike-price');
      if (totalPriceElement) {
        totalPriceElement.textContent = this.formatPrice(this.basePrice);
      }

      // Update product retail price display
      var retailPriceElement = document.querySelector('[data-product-retail-price]');
      if (retailPriceElement) {
        retailPriceElement.textContent = this.formatPrice(this.basePrice);
      }

      // Update mobile lease price display
      var mobileTextElement = document.getElementById('lease-price-mobile-text');
      if (mobileTextElement) {
        if (this.leaseType === 'prive' && promoPrice !== null) {
          mobileTextElement.textContent =
            'De eerste ' + this.promoMonthsText + ' €' + this.formatPriceNumber(promoPrice) +
            ' p/m, daarna €' + this.formatPriceNumber(monthlyPrice);
        } else {
          mobileTextElement.textContent = 'Prijs per maand €' + this.formatPriceNumber(monthlyPrice);
        }
      }

      this.updateComparisonPrices();
      this.updatePageTitle(monthlyPrice, promoPrice);
      this.notifyChange();

      debugLog('Lease price updated:', monthlyPrice, 'basis:', this.leaseBasePrice, '(type:', this.leaseType, ', term:', this.leaseTerm, ')');
    }

    /**
     * Meld lease type + prijzen aan andere scripts (o.a. snippets/leasewizard.liquid).
     * basePrice = verkoopprijs, leaseBasePrice = prijs waarop de lease berekend wordt,
     * zakelijkPrice = zakelijke netto maandprijs (36 mnd) van de gekozen variant.
     * Wie later laadt, leest dezelfde waarden direct van dit element.
     */
    notifyChange() {
      document.dispatchEvent(new CustomEvent('lease:updated', {
        detail: {
          leaseType: this.leaseType,
          basePrice: this.basePrice,
          leaseBasePrice: this.leaseBasePrice,
          zakelijkPrice: this.zakelijkPrice
        }
      }));
    }

    updatePageTitle(price, promoPrice) {
      var productTitleElement = document.querySelector('[data-product-title]');
      if (!productTitleElement) return;

      var title = productTitleElement.getAttribute('data-product-title');
      if (!title) return;

      var formattedPrice;
      if (this.leaseType === 'prive' && promoPrice !== null) {
        formattedPrice = this.formatPriceNumber(promoPrice);
        document.title = title + " - De eerste " + this.promoMonthsText + " €" + formattedPrice + " per maand";
      } else {
        formattedPrice = this.formatPriceNumber(price);
        document.title = title + " - Lease 'm voor €" + formattedPrice + " per maand";
      }
    }

    /**
     * Veilig renderen van het lease-prijsblok (zonder innerHTML).
     * Layout zoals het actie-prijsblok:
     *   [ Nu 3 maanden 50% korting ]         <- badge
     *   <s>€97,95</s>  €48,98                <- doorgestreepte normale prijs + grote actieprijs
     *   per maand
     *   Eerste 3 maanden, daarna €97,95 per maand
     *
     * @param {number} price             de normale maandprijs
     * @param {number|null} promoPrice   de actieprijs (privé), of null zonder actie
     */
    renderLeasePrice(element, price, promoPrice) {
      // Clear existing content
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }

      var normalStr = '€' + this.formatPriceNumber(price);

      // Zakelijk (of geen actie): gewoon de normale prijs tonen.
      if (this.leaseType === 'zakelijk' || promoPrice === null) {
        var zblock = document.createElement('div');
        zblock.className = 'lease-price-amount lease-promo-block';

        var zmain = document.createElement('div');
        zmain.className = 'lease-promo-main';
        var znow = document.createElement('span');
        znow.className = 'lease-promo-now';
        znow.textContent = normalStr;
        zmain.appendChild(znow);
        zblock.appendChild(zmain);

        var zsub = document.createElement('div');
        zsub.className = 'lease-promo-sub';
        zsub.textContent = this.leaseType === 'zakelijk' ? 'Netto kosten per maand' : 'per maand';
        zblock.appendChild(zsub);

        element.appendChild(zblock);
        return;
      }

      // Privé: actie-blok (badge + actieprijs + uitleg).
      var block = document.createElement('div');
      block.className = 'lease-price-amount lease-promo-block';

      // Badge
      var badge = document.createElement('span');
      badge.className = 'lease-promo-badge';
      badge.textContent = this.promoBadgeLabel;
      if (this.promo.bg) badge.style.background = this.promo.bg;
      if (this.promo.fg) badge.style.color = this.promo.fg;
      block.appendChild(badge);

      // Hoofdregel: doorgestreepte normale prijs + grote actieprijs
      var main = document.createElement('div');
      main.className = 'lease-promo-main';

      var was = document.createElement('s');
      was.className = 'lease-promo-was';
      was.textContent = normalStr;
      main.appendChild(was);

      var now = document.createElement('span');
      now.className = 'lease-promo-now';
      now.textContent = '€' + this.formatPriceNumber(promoPrice);
      main.appendChild(now);

      block.appendChild(main);

      // "per maand"
      var sub = document.createElement('div');
      sub.className = 'lease-promo-sub';
      sub.textContent = 'per maand';
      block.appendChild(sub);

      // "Eerste 3 maanden, daarna €97,95 per maand"
      var after = document.createElement('div');
      after.className = 'lease-promo-after';
      after.appendChild(document.createTextNode('Eerste ' + this.promoMonthsText + ', daarna '));
      var afterStrong = document.createElement('strong');
      afterStrong.textContent = normalStr;
      after.appendChild(afterStrong);
      after.appendChild(document.createTextNode(' per maand'));
      block.appendChild(after);

      element.appendChild(block);
    }

    updateComparisonPrices() {
      var privePriceElement = document.getElementById('prive_prijs');
      if (privePriceElement && this.privePrice !== null) {
        privePriceElement.textContent = this.formatPriceNumber(this.privePrice);
      }

      var zakelijkPriceElement = document.getElementById('zakelijk_prijs');
      if (zakelijkPriceElement && this.zakelijkPrice !== null) {
        zakelijkPriceElement.textContent = this.formatPriceNumber(this.zakelijkPrice);
      }
    }

    formatPriceNumber(price) {
      var validated = this.validateNumber(price, 0, 100000);
      if (validated === null) return '0,00';
      // NL-notatie: punt als duizendtalscheiding, komma als decimaalscheiding.
      // Relevant voor de adviesprijs (grote bedragen); maandprijzen blijven ongewijzigd.
      var parts = validated.toFixed(2).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts[0] + ',' + parts[1];
    }

    formatPrice(price) {
      return '€' + this.formatPriceNumber(price);
    }

    displayError() {
      var leaseDisplayElement = document.getElementById('lease-price-display');
      if (leaseDisplayElement) {
        leaseDisplayElement.textContent = 'Lease prijs niet beschikbaar';
        leaseDisplayElement.classList.add('error');
        delete leaseDisplayElement.dataset.currentLeasePrice;
        delete leaseDisplayElement.dataset.currentPromoPrice;
        delete leaseDisplayElement.dataset.currentPromoMonths;
      }
      debugLog('Error: Could not calculate lease price');
    }
  }

  if (!customElements.get('lease-calculator')) {
    customElements.define('lease-calculator', LeaseCalculator);
  }
})();
